import React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

/**
 * Error Boundary for Chart components
 * Catches runtime errors from lightweight-charts library (e.g., locale issues)
 * and displays a graceful fallback UI instead of crashing the page
 */
class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error for debugging
    console.error('[ChartErrorBoundary] Chart error caught:', error);
    console.error('[ChartErrorBoundary] Error info:', errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  render() {
    if (this.state.hasError) {
      const { symbol = 'Chart', height = '380px' } = this.props;
      
      return (
        <div 
          className="flex flex-col items-center justify-center p-6 rounded-xl"
          style={{ 
            height, 
            backgroundColor: 'rgba(255,255,255,0.02)', 
            border: '1px solid rgba(255,255,255,0.05)' 
          }}
        >
          <AlertCircle className="h-8 w-8 mb-3" style={{ color: '#f87171' }} />
          <p className="text-sm text-center mb-1" style={{ color: '#fafafa' }}>
            Chart temporarily unavailable
          </p>
          <p className="text-xs text-center mb-4" style={{ color: '#71717a' }}>
            {symbol} - There was an issue rendering the chart
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors hover:bg-white/10"
            style={{ backgroundColor: 'rgba(99, 102, 241, 0.15)', color: '#a5b4fc' }}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Try Again
          </button>
          <p className="text-[10px] mt-4 text-center max-w-xs" style={{ color: '#3f3f46' }}>
            This may be a temporary environment issue. Try refreshing the page.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChartErrorBoundary;
